import { makeActionCreator } from '../../../config/store/utils';
import {
  readParquet,
  readParquetGrafos,
} from '../../../services/productivityService';
import {
  getProductionTargets,
  saveProductionTargets,
} from '../../../services/monitoringService';

import {
  magenta70,
  cyan70,
  teal70,
  purple70,
  green70,
  coolGray70,
} from '@carbon/colors';

export const GET_DESPACHO = 'GET_DESPACHO';
export const GET_DESPACHO_ERROR = 'GET_DESPACHO_ERROR';
export const GET_DESPACHO_SUCCESS = 'GET_DESPACHO_SUCCESS';

export const onGetDespacho = makeActionCreator(GET_DESPACHO);
export const onGetDespachoError = makeActionCreator(
  GET_DESPACHO_ERROR,
  'payload'
);
export const onGetDespachoSuccess = makeActionCreator(
  GET_DESPACHO_SUCCESS,
  'payload'
);

// Helper function to calculate estado based on Python logic
const calculateEstado = truck => {
  // Get current time in UTC
  const currentTime = new Date();

  // Parse event_time_now (assuming it's in milliseconds)
  const eventTime = new Date(truck.event_time_now);

  // Calculate time difference in minutes
  const timeDiffMinutes = (currentTime - eventTime) / (1000 * 60);

  // Get kph, default to 0 if null/undefined
  const kph = truck.kph ?? 0;

  // Get geocerca in lowercase for case-insensitive comparison
  const geocercaLower = (truck.geocerca_total_now || '').toLowerCase();

  // Apply conditions in order (matches Python np.select logic)

  // Condition 1: Contains "taller" or "calama" AND kph <= 1
  if (
    (geocercaLower.includes('taller') || geocercaLower.includes('calama')) &&
    kph <= 1
  ) {
    return 'mantenimiento';
  }

  // Condition 2: Contains "casino" AND kph <= 1
  if (geocercaLower.includes('casino') && kph <= 1) {
    return 'en colacion';
  }

  // Condition 3: kph <= 1 AND time_diff <= 20
  if (kph <= 1 && timeDiffMinutes <= 20) {
    return 'detenido';
  }

  // Condition 4: kph <= 1 AND 20 < time_diff <= 60
  if (kph <= 1 && timeDiffMinutes > 20 && timeDiffMinutes <= 60) {
    return 'apagado reciente';
  }

  // Condition 5: kph <= 1 AND time_diff > 60
  if (timeDiffMinutes > 60) {
    return 'apagado prolongado';
  }

  // Default: operando
  return 'operando';
};

// Helper function to determine if a truck is assigned based on shift time
const isAssignedToCurrentShift = eventTimeNow => {
  if (!eventTimeNow) return false;

  const now = new Date();
  const eventTime = new Date(eventTimeNow);

  // Get current hour
  const currentHour = now.getHours();

  // Determine current shift start time
  let currentShiftStart;
  if (currentHour >= 8 && currentHour < 20) {
    // Day shift: 8 AM - 8 PM
    currentShiftStart = new Date(now);
    currentShiftStart.setHours(8, 0, 0, 0);
  } else {
    // Night shift: 8 PM - 8 AM
    if (currentHour >= 20) {
      // Currently in night shift that started today at 8 PM
      currentShiftStart = new Date(now);
      currentShiftStart.setHours(20, 0, 0, 0);
    } else {
      // Currently in night shift that started yesterday at 8 PM
      currentShiftStart = new Date(now);
      currentShiftStart.setDate(currentShiftStart.getDate() - 1);
      currentShiftStart.setHours(20, 0, 0, 0);
    }
  }

  // If event time is before current shift start, truck is not assigned
  return eventTime >= currentShiftStart;
};

const isMoreThanOneMonthOld = eventTimeMs => {
  if (!eventTimeMs) return true; // Treat missing timestamp as old

  const now = Date.now();
  const oneDayMs = 30 * 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const timeDiff = now - eventTimeMs;

  return timeDiff > oneDayMs;
};

const isMoreThan30MinutesOld = eventTimeMs => {
  if (!eventTimeMs) return true;
  const now = Date.now();
  const thirtyMinMs = 30 * 60 * 1000;
  return now - eventTimeMs > thirtyMinMs;
};

export const onGetDespachoThunk = () => async dispatch => {
  dispatch(onGetDespacho());
  try {
    const response = await readParquetGrafos({
      type: 'grafos-ruta/rutas-actuales',
    });
    const allTrucks = response.data.data;

    // Filter excluded trucks FIRST
    const excludedTrucks = allTrucks.filter(truck =>
      isMoreThanOneMonthOld(truck.event_time_now)
    );

    // Then filter active trucks into trucksArray
    const trucksArray = allTrucks.filter(
      truck => !isMoreThanOneMonthOld(truck.event_time_now)
    );

    // Calculate estado for each truck FIRST
    for (const truck of trucksArray) {
      truck.estado = calculateEstado(truck);
    }

    // Estado color mapping
    const ESTADO_COLOR = {
      'apagado prolongado': magenta70,
      detenido: cyan70,
      'en colacion': teal70,
      mantenimiento: purple70,
      operando: green70,
      'apagado reciente': coolGray70,
    };

    // Build a map of routes, keeping trucks split by direccion
    const routesMap = {};
    for (const truck of trucksArray) {
      const key = `${truck.lugar_carga} → ${truck.lugar_descarga}`;
      const dir =
        (truck.direccion || '').toLowerCase() === 'ida' ? 'ida' : 'vuelta';

      // Skip route assignment if data is older than 30 minutes
      if (isMoreThan30MinutesOld(truck.event_time_now)) continue; // ← only affects routes

      // Skip if truck has no activity in current shift (clean slate)
      if (!isAssignedToCurrentShift(truck.event_time_now)) continue;

      if (!routesMap[key]) {
        routesMap[key] = {
          origin: truck.lugar_carga,
          destination: truck.lugar_descarga,
          ida: [],
          vuelta: [],
        };
      }
      routesMap[key][dir].push(truck);
    }

    const byDistDesc = (a, b) => b.distancia - a.distancia;

    // For every route, create BOTH lanes: ida and vuelta
    const toPlatforms = [];
    const fromPlatforms = [];
    for (const route of Object.values(routesMap)) {
      const idaSorted = [...route.ida].sort(byDistDesc);
      const vueltaSorted = [...route.vuelta].sort(byDistDesc);

      toPlatforms.push({
        origin: route.origin,
        destination: route.destination,
        direccion: 'ida',
        trucks: idaSorted,
      });

      fromPlatforms.push({
        origin: route.origin,
        destination: route.destination,
        direccion: 'vuelta',
        trucks: vueltaSorted,
      });
    }

    // Optional: combined list, if other parts expect it
    const allRoutes = toPlatforms.concat(fromPlatforms);

    const HARDCODED_GEOFENCES = new Set([
      'Muro Norte',
      'Pretil Principal T1',
      'Pretil Cabecera 2',
      'Relleno Muro Oeste',
      'Taller Trek MB',
      'Taller Tassara Volvo',
      'Taller Tassara 2',
      'Taller Maquinaria',
      'Estacionamiento Maquinarias',
      'Estacionamiento Casino IIFF',
      'Estacionamiento Parqueo Bodeg',
      'Plataforma 2',
      'Plataforma 3',
      'Plataforma 1',
      'Velocidad Pretil Diagonal',
      'Relleno Pretil 2 NS',
      'Botadero Muro Sur',
      'Pretil 2 Sector 6',
      'Muro Sur Tramo 1',
      'Muro Sur Tramo 4',
      'Camino Muro Norte EBMN',
      'Pretil Principal T3',
      'Relleno Muro Noroeste 1',
      'EBMN',
      'Parqueo Plataforma 1',
      'Gasolinera 1',
      'Gasolinera 2',
      'Muro Sur Tramo 3',
      'Muro Sur Tramo 2',
      'Muro Suroriente 1',
      'Parqueo Plataforma 3',
      'Piscina Quebrada sur',
      'Linea Muro NO1',
      'Casino Plataforma 1 Velocidad',
    ]);

    // build new geofence panel
    const geofenceGroups = {};

    for (const truck of trucksArray) {
      const geo = truck.geocerca_total_now;

      // Only show if geofence is in static list
      if (!HARDCODED_GEOFENCES.has(geo)) continue;

      // color based on estado
      const estado = (truck.estado || '').trim();
      truck.estadoColor = ESTADO_COLOR[estado];

      // group by geofence
      if (!geofenceGroups[geo]) geofenceGroups[geo] = [];
      geofenceGroups[geo].push(truck);
    }

    // Build unassigned trucks list
    const unassignedTrucks = [];
    for (const truck of trucksArray) {
      // Check if truck is NOT in current shift
      if (!isAssignedToCurrentShift(truck.event_time_now)) {
        // Add color based on estado
        const estado = (truck.estado || '').trim().toLowerCase();
        truck.estadoColor = ESTADO_COLOR[estado] || coolGray70;

        unassignedTrucks.push(truck);
      }
    }

    return dispatch(
      onGetDespachoSuccess({
        fromPlatforms,
        toPlatforms,
        data: allRoutes,
        geofenceGroups: geofenceGroups,
        unassignedTrucks: unassignedTrucks,
        excludedTrucks: excludedTrucks,
      })
    );
  } catch (error) {
    console.log(error);
    return dispatch(onGetDespachoError({ error }));
  }
};

export const GET_PRODUCTION_TARGETS = 'GET_PRODUCTION_TARGETS';
export const GET_PRODUCTION_TARGETS_SUCCESS = 'GET_PRODUCTION_TARGETS_SUCCESS';
export const GET_PRODUCTION_TARGETS_ERROR = 'GET_PRODUCTION_TARGETS_ERROR';
export const SAVE_PRODUCTION_TARGETS = 'SAVE_PRODUCTION_TARGETS';
export const SAVE_PRODUCTION_TARGETS_SUCCESS =
  'SAVE_PRODUCTION_TARGETS_SUCCESS';
export const SAVE_PRODUCTION_TARGETS_ERROR = 'SAVE_PRODUCTION_TARGETS_ERROR';

/**
 * Get production targets thunk
 */
export const onGetProductionTargetsThunk = () => async dispatch => {
  try {
    dispatch({ type: GET_PRODUCTION_TARGETS });

    const response = await getProductionTargets();
    const targets = response.data?.targets || {};

    dispatch({
      type: GET_PRODUCTION_TARGETS_SUCCESS,
      payload: { targets },
    });
  } catch (error) {
    console.error('Error getting production targets:', error);
    dispatch({
      type: GET_PRODUCTION_TARGETS_ERROR,
      payload: { error: error.message },
    });
  }
};

/**
 * Save production targets thunk
 */
export const onSaveProductionTargetsThunk = targets => async dispatch => {
  try {
    dispatch({ type: SAVE_PRODUCTION_TARGETS });

    const response = await saveProductionTargets(targets);

    dispatch({
      type: SAVE_PRODUCTION_TARGETS_SUCCESS,
      payload: { targets },
    });

    return response;
  } catch (error) {
    console.error('Error saving production targets:', error);
    dispatch({
      type: SAVE_PRODUCTION_TARGETS_ERROR,
      payload: { error: error.message },
    });
    throw error;
  }
};
